import RMSvgIcon, {
  RMSvgIconProps,
} from 'components/customComponents/CommonComponents/RMSvgIcon';
import EViewType from 'enums/EViewType';
import React, { memo } from 'react';
import './NodeComponent.scss';
import { DraggableContainer } from '../../../DraggableContainer';
import { usePasteNodesHandler } from 'components/explorer/hooks';
import ETreeActionTypes from 'enums/ETreeActionTypes';
import useBroadcast, { CHANNEL_ACTIONS, CHANNEL_TYPES } from 'business/broadcast';
import { copyPasteValidation } from 'components/explorer/utils';
import { useRightClick } from 'components/rightClick/rightClickProvider';
import useRMLite from 'CustomHooks/useRMLite';
import { TreeNodeDTO } from 'business/models/Models';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';

interface nodeComponentProps {
  viewType?: EViewType;
  name: string;
  imageProps: RMSvgIconProps;
  locked?: boolean;
  datePrepared?: string;
  onClick?: any;
  notSeen?: boolean;
  selectedNodes: any[];
  node: any;
  selectedExplorerNode: TreeNodeDTO | null;
}
function NodeComponent(props: nodeComponentProps) {
  const {
    viewType = EViewType.ListView,
    name,
    locked,
    datePrepared = '01-01-2000',
    imageProps,
    onClick,
    notSeen,
    selectedNodes,
    node,
    selectedExplorerNode,
  } = props;

  const { handleRightClick } = useRightClick();
  const rmLite = useRMLite();

  const onContextMenuHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    const isQuickAcess = selectedExplorerNode?.type === ETreeNodeTypes.QuickAccess;
    if (isQuickAcess) {
      return;
    }
    // Select the node first if it's not already selected
    if (onClick && !isNodeSelected) {
      onClick({ detail: 1 });
    }
    handleRightClick(e);
  };

  const isNodeSelected = selectedNodes.find((n) => n?.id === node.id);
  const isLastSelected =
    isNodeSelected && isNodeSelected === selectedNodes[selectedNodes.length - 1];
  const pasteNodesHandler = usePasteNodesHandler();
  const explorerBroadcastChannel = useBroadcast(CHANNEL_TYPES.EXPLORER);

  const postRefreshMessage = (sourceNode) => () => {
    explorerBroadcastChannel.postMessage({
      action: CHANNEL_ACTIONS.EXPLORER_ADD_NODE,
      payload: { parentId: node.id },
    });
    explorerBroadcastChannel.postMessage({
      action: CHANNEL_ACTIONS.EXPLORER_ADD_NODE,
      payload: { parentId: sourceNode.parentId },
    });
  };
  const handleDrop = (nodes: any[], operationType: ETreeActionTypes) => {
    if (nodes) {
      pasteNodesHandler(operationType, nodes, node, postRefreshMessage(nodes[0]), false);
    }
  };

  const handleOnDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (selectedNodes && onClick && !selectedNodes.find((n) => n.id === node.id)) {
      props.onClick({ detail: 1 });
    }
  };

  const validateDropFile = (draggedNodes, currentNode) => {
    if (!draggedNodes) {
      return false;
    }
    return copyPasteValidation(draggedNodes, currentNode).unableToPasteNodes.length <= 0;
  };

  const component = (
    <li
      id={`nodecomponent-wrapper-${node.id}`}
      /**
       * data-id is used to identify the node in the drag selection box, DO NOT CHANGE IT
       */
      data-id={node.id}
      data-testid={`nodecomponent-wrapper-${node.id}`}
      className={`selectable-item node-main-container-${viewType} 
        ${isLastSelected ? 'node-container-last-focused' : ''} 
        ${isNodeSelected && !isLastSelected ? 'node-container-focused' : ''}`}
      onClick={onClick}
      onContextMenu={onContextMenuHandler}
    >
      <div
        id={`nodecomponent-content-container-${node.id}`}
        data-testid={`nodecomponent-content-container-${node.id}`}
        className={`node-component-content-container-${viewType}`}
        onContextMenu={onContextMenuHandler}
      >
        <div
          id={`nodecomponent-image-wrapper-${node.id}`}
          data-testid={`nodecomponent-image-wrapper-${node.id}`}
          className={`node-component-image-${viewType}`}
        >
          {}
          <RMSvgIcon
            id={`nodecomponent-image-svg-icon-${node.id}`}
            data-testid={`nodecomponent-image-svg-icon-${node.id}`}
            {...imageProps}
          />
        </div>
        <div
          id={`nodecomponent-text-wrapper-${node.id}`}
          data-testid={`nodecomponent-text-wrapper-${node.id}`}
          className={`node-component-image-container-${viewType}`}
        >
          <div
            id={`nodecomponent-name-${node.id}`}
            data-testid={`nodecomponent-name-${node.id}`}
            className={`node-component-text-${viewType} ${
              locked && 'node-component-text-locked'
            } ${notSeen && 'font-bold'}`}
          >
            {name}
          </div>

          {viewType === EViewType.ContentView ? (
            <div
              id={`nodecomponent-date-${node.id}`}
              data-testid={`nodecomponent-date-${node.id}`}
              className="node-component-date-text"
            >
              {'Date Created: ' + (datePrepared || '-')}
            </div>
          ) : null}
        </div>
      </div>
    </li>
  );

  return rmLite ? (
    component
  ) : (
    <DraggableContainer
      node={node}
      nodeName={node.name}
      handleDrop={handleDrop}
      onDragStart={handleOnDragStart}
      selectedFiles={selectedNodes}
      validateDrop={validateDropFile}
    >
      {component}
    </DraggableContainer>
  );
}

export default memo(NodeComponent);
